import {
    Bounds,
    FeatureCollectionWithSprites,
    NamedLayer,
    SourceType,
    TypedDataLayerDto
} from '@flyfreely-portal-ui/flyfreely';
import { Feature, FeatureCollection } from 'geojson';
import mapboxgl, {
    CirclePaint,
    CustomLayerInterface,
    GeoJSONSourceRaw,
    Layer,
    SymbolLayout,
    SymbolPaint
} from 'mapbox-gl';
import { FeatureProperties } from '../constants';
import { FeatureGroup, MapFeature, MapGeometry } from '../interfaces';

export function createGeoJsonSource(group: FeatureGroup): GeoJSONSourceRaw {
    if (group.type === 'Point') {
        return {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            },
            cluster: true,
            clusterMaxZoom: 16,
            clusterRadius: 32
        };
    }
    return {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: []
        }
    };
}

/**
 * Create the layers for this feature group. One of the layers must have the same ID as
 * the feature layer id so that we can easily detect if they are drawn or not.
 * @param featureLayerId the ID of the feature group
 * @param group the feature group
 */
export function createLayers(
    featureLayerId: string,
    group: FeatureGroup
): (Layer | CustomLayerInterface)[] {
    return group.type === 'Point'
        ? createPointLayers(featureLayerId, group)
        : createPolygonLayers(featureLayerId, group);
}

function createPolygonLayers(
    featureLayerId: string,
    group: FeatureGroup
): (Layer | CustomLayerInterface)[] {
    const fillStyles = group?.styles?.fill ?? [{}];
    const symbolStyles = group?.styles?.symbol ?? [{}];
    const lineStyles = group?.styles?.line ?? [{}];

    const result: (Layer | CustomLayerInterface)[] = [
        ...fillStyles.map(
            (p, index) =>
                ({
                    id: `${featureLayerId}_${index}`,
                    source: featureLayerId,
                    type: 'fill',
                    paint: {
                        'fill-color': '#900',
                        'fill-opacity': 0.4,
                        ...p?.paint
                    },
                    layout: { ...p?.layout },
                    filter: getFilter(
                        ['==', ['geometry-type'], 'Polygon'],
                        p.filter
                    )
                } as Layer)
        ),
        ...symbolStyles.map(
            (p, index) =>
                ({
                    id: `${featureLayerId}-point_${index}`,
                    source: featureLayerId,
                    type: 'symbol',
                    layout: {
                        ...p?.layout
                    } as SymbolLayout,
                    paint: { ...p?.paint },
                    filter: p.filter
                } as Layer)
        ),
        ...lineStyles.map(
            (p, index) =>
                ({
                    id: `${featureLayerId}-line_${index}`,
                    source: featureLayerId,
                    type: 'line',
                    layout: { ...p?.layout },
                    paint: {
                        'line-color': '#ff0000',
                        'line-width': [
                            'case',
                            ['boolean', ['feature-state', 'highlight'], false],
                            2,
                            1
                        ],
                        ...p?.paint
                    },
                    filter: getFilter(
                        ['!=', ['geometry-type'], 'Point'],
                        p.filter
                    )
                } as Layer)
        )
    ];

    // Add origin (X) and end (arrow) marker layers for measurement lines
    if (group.type === 'MeasurementLine') {
        result.push(
            {
                id: `${featureLayerId}-origin`,
                source: featureLayerId,
                type: 'symbol',
                filter: [
                    '==',
                    ['get', 'meta'],
                    FeatureProperties.MEASUREMENT_LINE_ORIGIN
                ],
                layout: {
                    'text-field': '✕',
                    'text-anchor': 'center',
                    'text-size': 20,
                    'text-allow-overlap': true
                } as SymbolLayout,
                paint: {
                    'text-color': 'rgba(0, 0, 0, 1)',
                    'text-halo-color': 'rgba(255, 255, 255, 1)',
                    'text-halo-width': 3,
                    'text-halo-blur': 1
                } as SymbolPaint
            } as Layer,
            {
                id: `${featureLayerId}-end`,
                source: featureLayerId,
                type: 'symbol',
                filter: [
                    '==',
                    ['get', 'meta'],
                    FeatureProperties.MEASUREMENT_LINE_END
                ],
                layout: {
                    'text-field': '▶',
                    'text-anchor': 'center',
                    'text-size': 16,
                    'text-allow-overlap': true,
                    'text-rotate': ['get', 'bearing'],
                    'text-rotation-alignment': 'map'
                } as SymbolLayout,
                paint: {
                    'text-color': 'rgba(0, 0, 0, 1)',
                    'text-halo-color': 'rgba(255, 255, 255, 1)',
                    'text-halo-width': 3,
                    'text-halo-blur': 1
                } as SymbolPaint
            } as Layer
        );
    }

    return result;
}

function createPointLayers(
    featureLayerId: string,
    group: FeatureGroup
): (Layer | CustomLayerInterface)[] {
    const pointStyles = group?.styles?.symbol ?? [{}];

    const circlePaint: CirclePaint = {
        'circle-color': {
            property: 'point_count',
            type: 'interval',
            stops: [[0, '#005fa2']]
        },
        'circle-radius': {
            property: 'point_count',
            type: 'interval',
            stops: [
                [0, 15],
                [100, 20],
                [750, 25]
            ]
        }
    };

    const markerPaint: SymbolPaint = {};

    const result: (Layer | CustomLayerInterface)[] = [
        {
            paint: circlePaint,
            id: `${featureLayerId}-clustered`,
            source: featureLayerId,
            type: 'circle',
            filter: ['has', 'point_count']
        },
        {
            // paint: circlePaint,
            id: `${featureLayerId}-clustered-text`,
            source: featureLayerId,
            type: 'symbol',
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12
            } as SymbolLayout,
            paint: {
                'text-color': '#ffffff',
                'text-opacity': 1
            },
            filter: ['has', 'point_count']
        },
        ...pointStyles.map(
            (p, index) =>
                ({
                    id: `${featureLayerId}_${index}`,
                    source: featureLayerId,
                    type: 'symbol',
                    filter: getFilter(['!', ['has', 'point_count']], p.filter),
                    layout: {
                        'icon-image': 'flight-marker',
                        'icon-size': 0.75,
                        'icon-anchor': 'bottom',
                        'icon-allow-overlap': true,
                        ...p?.layout
                    } as SymbolLayout,
                    paint: markerPaint
                } as Layer)
        ),
        {
            // This is the generic label text formatting for points, including drawing points
            id: `${featureLayerId}-text`,
            source: featureLayerId,
            type: 'symbol',
            layout: {
                'text-field': '{name}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
                'text-offset': [0, 1]
            } as SymbolLayout,
            paint: {
                'text-color': '#ffffff',
                'text-opacity': 1
            },
            filter: ['!', ['has', 'point_count']]
        }
    ];

    return result;
}
/**
 * Given a default filter and custom filter, build a new filter
 * It assumes an `all`, thus an AND query. If need be, this can be made a parameter as well.
 */
function getFilter(defaultFilter: any[], customFilter: any[]) {
    if (!customFilter || customFilter.length === 0) {
        return defaultFilter;
    }
    return ['all', [...defaultFilter], [...customFilter]];
}

export function calculateClusterGroupAndFeatures(
    group: FeatureGroup,
    features: GeoJSON.Feature<GeoJSON.Geometry>[]
) {
    const clusterFeatureIds = features.map(f => f.id);
    const domainFeatures = group.existingFeatures.filter(
        f => clusterFeatureIds.indexOf(f.id) !== -1
    );

    return {
        groupId: group.id,
        features: domainFeatures
    };
}

export function calculateSourceId(group: FeatureGroup) {
    return calculateSourceIdFromId(group.id);
}

export function calculateSourceIdFromId(id: number | string) {
    return `feature_group_${id}`;
}

export function colourGenerator(ix: number) {
    const colours = [
        '#4c5916',
        '#40ffbf',
        '#005580',
        '#260d33',
        '#ff4400',
        '#664d1a',
        '#55f23d',
        '#00a69b',
        '#40a6ff',
        '#b630bf',
        '#7f2200',
        '#bfa330',
        '#00a642',
        '#165559',
        '#3056bf',
        '#f200a2',
        '#a65800',
        '#ffee00',
        '#0d331c',
        '#30a3bf',
        '#4040ff',
        '#800055',
        '#331b00',
        '#85a600',
        '#005930',
        '#0d2b33',
        '#110080',
        '#b22d3e'
    ];

    return colours[ix % colours.length];
}

/**
 * Generate a GeoJSON feature response
 * @param features the features
 * @param visibleFeatures the visible feature IDs
 * @param groupId the ID of the group
 */
export function getFeatures(
    features: MapFeature[],
    visibleFeatures: Set<number | string>,
    groupId: number
): Feature[] {
    const filteredFeatures = features.filter(
        f =>
            visibleFeatures.has(f.id) &&
            f.geom != null &&
            ((f.geom.type === 'GeometryCollection' &&
                f.geom.geometries != null) ||
                (f.geom.type !== 'GeometryCollection' &&
                    f.geom.coordinates != null))
    );
    const createFeature = (feat: MapFeature): Feature => {
        return {
            id: feat.id,
            type: 'Feature',
            geometry: feat.geom,
            properties: {
                ...feat.properties,
                name: feat.name,
                'category-id': feat.categoryId
            },
            data: groupId
        } as Feature;
    };
    const childFeatures: Feature[] = [].concat(
        ...filteredFeatures
            .filter(f => f.children != null)
            .map(f => f.children.map(createFeature))
    );
    const result = [...filteredFeatures.map(createFeature), ...childFeatures];
    return result;
}

export function isValidGeometry(geom: MapGeometry) {
    if (geom == null) {
        return false;
    }
    if (geom.type === 'GeometryCollection') {
        return geom.geometries != null;
    }

    return geom.coordinates != null;
}

/**
 * Flatten a list of data layers down to the concrete list of layers.
 * @param dataGroupList
 * @param enabledLayerGroups the layer group identifiers that should be included
 * @returns
 */
export function extractFlattenedLayers(
    dataGroupList: TypedDataLayerDto[],
    enabledLayerGroups: string[]
) {
    return dataGroupList.reduce(
        (acc, l) =>
            acc.concat(
                <NamedLayer[]>l.layers.groups.reduce(
                    (acc2, l2) =>
                        // Only pass airspace layer groups to the map for rendering if they have a null identifier,
                        // or if they are activated via the "enabledLayerGroups" input.
                        l2.identifier == null ||
                        enabledLayerGroups?.includes(l2.identifier)
                            ? acc2.concat(
                                  l2.layers.filter(layer => layer.id != null)
                              )
                            : acc2,
                    l.layers.ungrouped.filter(layer => layer.id != null)
                )
            ),
        []
    );
}

/**
 * Adjust the value to be within the min and max values.
 *
 * @param value the value to be clamped
 * @param range the max/min value of the range
 */
function clamp(value: number, range: number) {
    if (value > range) {
        while (value > range) {
            value = value - 2 * range;
        }
        return value;
    }
    if (value < -range) {
        while (value < -range) {
            value = value + 2 * range;
        }
        return value;
    }
    return value;
}

/**
 * Convert a mapbox bounds object to a GeoJSON polygon using the four corners, ignoring any geodesic lines.
 *
 * This takes account of the antimeridian for a map that is not rotated. If the map is going to be rotated, we need
 * to properly cut the polygon with a line string.
 * @param bound the latlong bounds
 */
export function asPolygon(bound: mapboxgl.LngLatBounds): GeoJSON.MultiPolygon {
    if (bound.isEmpty()) {
        return {
            type: 'MultiPolygon',
            coordinates: []
        };
    }

    const sw = bound.getSouthWest();
    const ne = bound.getNorthEast();

    if (sw.lng < -180 || ne.lng > 180) {
        return {
            type: 'MultiPolygon',
            coordinates: [
                [
                    [
                        [clamp(sw.lng, 180), sw.lat],
                        [180, sw.lat],
                        [180, ne.lat],
                        [clamp(sw.lng, 180), ne.lat],
                        [clamp(sw.lng, 180), sw.lat]
                    ]
                ],
                [
                    [
                        [-180, sw.lat],
                        [clamp(ne.lng, 180), sw.lat],
                        [clamp(ne.lng, 180), ne.lat],
                        [-180, ne.lat],
                        [-180, sw.lat]
                    ]
                ]
            ]
        };
    } else {
        return {
            type: 'MultiPolygon',
            coordinates: [
                [
                    [
                        [sw.lng, sw.lat],
                        [ne.lng, sw.lat],
                        [ne.lng, ne.lat],
                        [sw.lng, ne.lat],
                        [sw.lng, sw.lat]
                    ]
                ]
            ]
        };
    }
}

/**
 * Converts a Mapbox LngLatBounds object to a Bounds interface and optionally expands
 * the bounds by a specified percentage in all directions.
 *
 * For non-crossing viewports:
 * - Latitude (north/south) is clamped to ±90° after expansion.
 *
 * @param bounds - The Mapbox LngLatBounds object containing the original geographic bounds
 * @param expandByPercentage - Optional percentage (0-100) to expand the bounds by.
 *                            Defaults to 0 (no expansion). For example, 100 means expand
 *                            by 100% in all directions (doubling the visible span).
 *
 * @returns A Bounds object with west, south, east, north coordinates.
 **/
export function mapAndExpandBounds(
    bounds: mapboxgl.LngLatBounds,
    expandByPercentage: number = 0
): Bounds {
    const northBound = bounds.getNorth();
    const southBound = bounds.getSouth();
    const eastBound = bounds.getEast();
    const westBound = bounds.getWest();

    // NS expansion — latitude cannot cross a pole, so this is always safe.
    const northSouthSpan = Math.abs(northBound - southBound);
    const nsExpansion = (northSouthSpan * (expandByPercentage / 100)) / 2;
    const north = Math.min(90, northBound + nsExpansion);
    const south = Math.max(-90, southBound - nsExpansion);

    // EW expansion - don't clamp anything, API will handle the date line.
    const eastWestSpan = Math.abs(eastBound - westBound);
    const ewExpansion = (eastWestSpan * (expandByPercentage / 100)) / 2;
    const east = eastBound + ewExpansion;
    const west = westBound - ewExpansion;

    return { north, south, east, west };
}

/**
 * Determines whether a Mapbox LngLatBounds object falls completely inside a Bounds object.
 *
 * Mapbox uses a continuous (unwrapped) coordinate system: a viewport that straddles the
 * international date line will have `getEast() > 180` or `getWest() < -180`. This function
 * detects that situation using the same convention as `asPolygon()`.
 *
 * When the current viewport crosses the antimeridian the cached `Bounds` must cover the
 * full-longitude range (west ≤ −180, east ≥ 180) — as produced by `mapAndExpandBounds()`
 * for the same scenario — for the containment check to succeed.
 *
 * @param mapboxBounds - The Mapbox LngLatBounds object to check (current viewport)
 * @param bounds - The Bounds object defining the cached/container boundaries
 * @returns true if mapboxBounds falls completely inside bounds, false otherwise
 */
export function isMapboxBoundsInsideBounds(
    mapboxBounds: mapboxgl.LngLatBounds,
    bounds: Bounds
): boolean {
    if (mapboxBounds.isEmpty()) {
        return true;
    }

    const mapboxNorth = mapboxBounds.getNorth();
    const mapboxSouth = mapboxBounds.getSouth();
    const mapboxEast = mapboxBounds.getEast();
    const mapboxWest = mapboxBounds.getWest();

    // NS check is always straightforward — latitude has no wraparound equivalent.
    const isNorthInside = mapboxNorth <= bounds.north;
    const isSouthInside = mapboxSouth >= bounds.south;
    if (!isNorthInside || !isSouthInside) {
        return false;
    }

    // Detect antimeridian crossing using the same convention as asPolygon():
    // Mapbox represents a dateline-crossing viewport with east > 180 or west < -180.
    if (mapboxEast > 180 || mapboxWest < -180) {
        // The cached bounds must span the full longitude band to contain this viewport.
        return bounds.west <= -180 && bounds.east >= 180;
    }

    // Normal (non-crossing) EW check.
    return mapboxEast <= bounds.east && mapboxWest >= bounds.west;
}

export function idListsDiffer(
    a: { id: number }[],
    b: { id: number }[]
): boolean {
    return (
        a.length !== b.length || !a.every(a_ => b.some(b_ => a_.id === b_.id))
    );
}

/**
 * Returns the elements in array a that are not in array b.
 * @param a The first array.
 * @param b The second array.
 * @returns The elements in array a that are not in array b.
 */
export function diff<T>(a: T[], b: T[]): T[] {
    return a.filter(x => !b.includes(x));
}

/**
 * Remove null values and replace with undefined.
 *
 * @param source the original source
 * @returns a tided up source
 */
export function fixSource<T extends SourceType>(source: T): T {
    if (source.type === 'geojson') {
        return {
            ...source,
            data: source.data ?? undefined,
            url: source.url ?? undefined,
            cluster: source.cluster,
            clusterMaxZoom: source.clusterMaxZoom ?? undefined,
            maxzoom: source.maxzoom ?? undefined
        };
    }
    return source;
}

/**
 * Sorts an array of objects by DISP_ORDER
 *
 * Sorting rules:
 *    - Items with DISP_ORDER values take precedence over items without values
 *    - When both have values, sort by numerical value in ascending order
 *    - When both have no values, maintain original relative order
 *
 * @param items Array of objects to be sorted
 * @returns Sorted array
 */
export function sortByDisplayOrder<
    T extends { properties?: { [key: string]: any } }
>(items: T[]): T[] {
    return items.sort((a, b) => {
        const dispOrderA = a.properties?.['DISP_ORDER'];
        const dispOrderB = b.properties?.['DISP_ORDER'];

        if (dispOrderA != null && dispOrderB != null) {
            return dispOrderA - dispOrderB;
        } else if (dispOrderA != null && dispOrderB == null) {
            return -1; // Items with DISP_ORDER appear first
        } else if (dispOrderA == null && dispOrderB != null) {
            return 1; // Items without DISP_ORDER appear later
        } else {
            return 0; // Both have no DISP_ORDER, maintain relative position
        }
    });
}

const LAYER_LEVEL_ORDER: Record<string, number> = {
    BASE: 0,
    BOTTOM: 1,
    MIDDLE: 2,
    TOP: 3
};

const LAYER_TYPE_ORDER: Record<string, number> = {
    raster: 0,
    area: 1,
    line: 2,
    circle: 3,
    symbol: 4
};

/**
 * Sort an array of NamedLayers by `layerLevel` (BASE → BOTTOM → MIDDLE → TOP) and then
 * by `layerWeight` (ascending) within each level, and finally by feature type.
 * Layers without these properties default to MIDDLE / 0, matching the
 * normalisation applied during style loading.
 *
 * The original array is not mutated; a new sorted array is returned.
 */
export function sortLayersByLevelAndWeight(layers: NamedLayer[]): NamedLayer[] {
    return [...layers].sort((a, b) => {
        const levelA = LAYER_LEVEL_ORDER[a.layerLevel ?? 'MIDDLE'] ?? 1;
        const levelB = LAYER_LEVEL_ORDER[b.layerLevel ?? 'MIDDLE'] ?? 1;
        if (levelA !== levelB) {
            return levelA - levelB;
        }

        const layerWeight = (a.layerWeight ?? 0) - (b.layerWeight ?? 0);
        if (layerWeight !== 0) {
            return layerWeight;
        }

        return (
            (LAYER_TYPE_ORDER[a.type] ?? 1) - (LAYER_TYPE_ORDER[b.type] ?? 1)
        );
    });
}

export function mergeFeatureCollections(
    collectionList: FeatureCollection[]
): FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: collectionList.reduce<FeatureCollection['features']>(
            (acc, collection) => acc.concat(collection.features),
            []
        )
    };
}

export function mergeFeatureCollectionWithSprites(
    collections?: FeatureCollectionWithSprites[]
): FeatureCollectionWithSprites {
    if (collections == null) {
        return {
            type: 'FeatureCollectionWithSprites',
            featureCollection: {
                type: 'FeatureCollection',
                features: []
            },
            spriteList: []
        };
    }
    return {
        type: 'FeatureCollectionWithSprites',
        featureCollection: mergeFeatureCollections(
            collections.filter(c => c != null).map(c => c.featureCollection)
        ),
        spriteList: collections
            .filter(c => c != null)
            .reduce<FeatureCollectionWithSprites['spriteList']>(
                (acc, collection) => acc.concat(collection.spriteList),
                []
            )
    };
}
